import { Flow } from 'signup-constants/Flow';
import { isntExistingUserExistingAccount } from '../app/verificationTypeSelectors';
import { areTherePostAccountCreationSteps, onlyMobileAppPromptStep } from '../flow-management/stepNumberingSelectors';
import { EndpointKey } from '../http/EndpointKey';
import { getHasRefreshedNewPortalAuth } from '../store/app/selectors';
import { arePortalExperimentsFetched } from '../store/experiments/selectors';
import { hasPostExistingAccountSelectionQuestions, isHSFXFLow } from '../store/flow/selectors';
import { isEndpointStatusSuccess } from '../store/http-responses/selectors';
import { isCopyCarouselFinished } from '../store/loading-status/selectors';
import { getIsMobile } from '../utils/deviceUtils';
import { isFlow } from '../utils/isFlow';
import BaseLoadingView from './BaseLoadingView';
import IllustrationLoadingView from './IllustrationLoadingView';
import { LoadingKey } from './LoadingKey';
import { getSignupRequestLoadingImageConfig } from './loadingUtils';
import imageUrls from '../images/imageUrls';
import { jsx as _jsx } from "react/jsx-runtime";
const baseLoadingView = ({
  loadingKey,
  hasSpinner,
  imageUrl,
  imageClass,
  textDisplayDelay,
  isIllustration,
  hasConfetti,
  illustrationHeight,
  hasLoadingDots,
  setTimeoutForCopyCarousel
}) => () => /*#__PURE__*/_jsx(BaseLoadingView, {
  hasConfetti: hasConfetti,
  hasLoadingDots: hasLoadingDots,
  hasSpinner: hasSpinner,
  illustrationHeight: illustrationHeight,
  imageClass: imageClass,
  imageUrl: imageUrl,
  isIllustration: isIllustration,
  loadingKey: loadingKey,
  setTimeoutForCopyCarousel: setTimeoutForCopyCarousel,
  textDisplayDelay: textDisplayDelay
});
const illustrationLoadingView = ({
  loadingKey,
  hasSpinner = false,
  illustration,
  imageClass = '',
  textDisplayDelay = 0,
  hasConfetti
}) => () => /*#__PURE__*/_jsx(IllustrationLoadingView, {
  hasConfetti: hasConfetti,
  hasSpinner: hasSpinner,
  illustration: illustration,
  imageClass: imageClass,
  loadingKey: loadingKey,
  textDisplayDelay: textDisplayDelay
});
const simpleLoadingScreen = ({
  loadingKey,
  imageUrl = '',
  hasSpinner = true,
  shouldClearLoading,
  imageClass = '',
  canSwitchCopy = () => true,
  shouldNotOverrideOtherLoading = () => false,
  textDisplayDelay = 0,
  shouldShowProgressBar = () => false,
  isIllustration = false,
  hasConfetti = false,
  illustrationHeight,
  hasLoadingDots = false,
  setTimeoutForCopyCarousel = 0
}) => ({
  [loadingKey]: {
    view: baseLoadingView({
      loadingKey,
      imageUrl,
      hasSpinner,
      imageClass,
      textDisplayDelay,
      isIllustration,
      hasConfetti,
      illustrationHeight,
      hasLoadingDots,
      setTimeoutForCopyCarousel
    }),
    shouldClearLoading,
    canSwitchCopy,
    shouldNotOverrideOtherLoading,
    shouldShowProgressBar
  }
});
const illustrationLoading = ({
  loadingKey,
  hasSpinner,
  shouldClearLoading,
  imageClass,
  shouldNotOverrideOtherLoading = () => false,
  textDisplayDelay = 0,
  illustration,
  shouldShowProgressBar = () => false,
  hasConfetti = false
}) => ({
  [loadingKey]: {
    view: illustrationLoadingView({
      loadingKey,
      illustration,
      hasSpinner,
      imageClass,
      textDisplayDelay,
      hasConfetti
    }),
    shouldClearLoading,
    shouldNotOverrideOtherLoading,
    shouldShowProgressBar
  }
});
const makeSimpleLoadingScreens = () => {
  return Object.keys(LoadingKey).reduce((obj, loadingKey) => Object.assign({}, obj, simpleLoadingScreen({
    loadingKey: loadingKey
  })), {});
};
export const LoadingScreen = Object.assign({}, makeSimpleLoadingScreens(), simpleLoadingScreen({
  loadingKey: LoadingKey.LoginAfterSignup,
  hasSpinner: true,
  textDisplayDelay: 1500,
  shouldNotOverrideOtherLoading: state => !areTherePostAccountCreationSteps(state),
  shouldClearLoading: () => getIsMobile(),
  canSwitchCopy: state => {
    return getHasRefreshedNewPortalAuth(state);
  }
}), illustrationLoading({
  loadingKey: LoadingKey.PartnerExistingAccount,
  illustration: 'bolt',
  shouldClearLoading: state => {
    return isFlow(Flow.DirectoryListing) && isEndpointStatusSuccess(EndpointKey.PartnerLookup)(state);
  }
}), simpleLoadingScreen(Object.assign({
  loadingKey: LoadingKey.SignupRequest
}, getSignupRequestLoadingImageConfig(), {
  hasSpinner: false,
  hasConfetti: true,
  shouldClearLoading: state => {
    if (!areTherePostAccountCreationSteps(state)) {
      return false;
    }
    const copyCarouselCheck = !onlyMobileAppPromptStep(state) ? isCopyCarouselFinished(state, LoadingKey.SignupRequest) : true;
    return isEndpointStatusSuccess(EndpointKey.Signup)(state) && copyCarouselCheck && (arePortalExperimentsFetched(state) || onlyMobileAppPromptStep(state));
  },
  canSwitchCopy: state => {
    return isEndpointStatusSuccess(EndpointKey.Signup)(state) && areTherePostAccountCreationSteps(state) && !onlyMobileAppPromptStep(state);
  },
  shouldShowProgressBar: isHSFXFLow
})), simpleLoadingScreen({
  loadingKey: LoadingKey.ACQ142SignupRequest,
  hasSpinner: false,
  hasConfetti: false,
  imageUrl: imageUrls.globe,
  imageClass: 'AirplaneGif',
  shouldClearLoading: state => {
    if (!areTherePostAccountCreationSteps(state)) {
      return false;
    }
    const copyCarouselCheck = isCopyCarouselFinished(state, LoadingKey.ACQ142SignupRequest);
    return isEndpointStatusSuccess(EndpointKey.Signup)(state) && (arePortalExperimentsFetched(state) || onlyMobileAppPromptStep(state)) && copyCarouselCheck;
  },
  canSwitchCopy: state => {
    return isEndpointStatusSuccess(EndpointKey.Signup)(state);
  },
  shouldShowProgressBar: () => {
    return true;
  }
}), simpleLoadingScreen({
  loadingKey: LoadingKey.ACQ142VariantBSignupRequest,
  hasSpinner: false,
  hasConfetti: true,
  isIllustration: true,
  imageUrl: 'success-green',
  shouldClearLoading: state => {
    if (!areTherePostAccountCreationSteps(state)) {
      return false;
    }
    return isEndpointStatusSuccess(EndpointKey.Signup)(state) && (arePortalExperimentsFetched(state) || onlyMobileAppPromptStep(state));
  },
  canSwitchCopy: () => {
    return false;
  },
  shouldShowProgressBar: () => {
    return true;
  }
}), simpleLoadingScreen({
  loadingKey: LoadingKey.ACQ142LoginAfterSignup,
  hasSpinner: false,
  hasConfetti: true,
  textDisplayDelay: 0,
  imageUrl: imageUrls.welcomeImage,
  shouldNotOverrideOtherLoading: state => !areTherePostAccountCreationSteps(state),
  shouldClearLoading: () => getIsMobile(),
  canSwitchCopy: () => {
    return false;
  }
}), simpleLoadingScreen({
  loadingKey: LoadingKey.ACQ142LoginAfterSignupMS,
  hasSpinner: true,
  textDisplayDelay: 0,
  shouldNotOverrideOtherLoading: state => !areTherePostAccountCreationSteps(state),
  shouldClearLoading: () => getIsMobile(),
  canSwitchCopy: () => {
    return false;
  }
}), simpleLoadingScreen({
  loadingKey: LoadingKey.PostSignupLoading,
  hasSpinner: true,
  shouldNotOverrideOtherLoading: state => !hasPostExistingAccountSelectionQuestions(state) || isntExistingUserExistingAccount(state),
  shouldClearLoading: state => !hasPostExistingAccountSelectionQuestions(state),
  shouldShowProgressBar: isHSFXFLow
}), simpleLoadingScreen(Object.assign({
  loadingKey: LoadingKey.GoogleContactsSyncLoading
}, getSignupRequestLoadingImageConfig(), {
  hasSpinner: false,
  hasConfetti: true,
  shouldClearLoading: state => {
    const copyCarouselCheck = isCopyCarouselFinished(state, LoadingKey.GoogleContactsSyncLoading);
    const isSyncActivated = isEndpointStatusSuccess(EndpointKey.GoogleContactsActivateSync)(state);
    return copyCarouselCheck && isSyncActivated;
  }
})), simpleLoadingScreen({
  loadingKey: LoadingKey.ACQ143SignupRequest,
  hasSpinner: false,
  hasConfetti: false,
  hasLoadingDots: true,
  shouldClearLoading: state => {
    if (!areTherePostAccountCreationSteps(state)) {
      return false;
    }
    return isEndpointStatusSuccess(EndpointKey.Signup)(state) && (arePortalExperimentsFetched(state) || onlyMobileAppPromptStep(state));
  },
  shouldShowProgressBar: () => true
}), simpleLoadingScreen({
  loadingKey: LoadingKey.ACQ143PortalCreated,
  hasSpinner: false,
  hasLoadingDots: true,
  setTimeoutForCopyCarousel: 1000,
  shouldClearLoading: state => {
    if (!areTherePostAccountCreationSteps(state)) {
      return false;
    }
    return isEndpointStatusSuccess(EndpointKey.LoginNewPortal)(state);
  },
  shouldShowProgressBar: () => true
}));
export const getLoading = loadingKey => {
  return LoadingScreen[loadingKey];
};